import { useContext } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { LivePresenceContext } from "./LivePresenceContext";
import { firstNameOf } from "./stageNotice";
import type { PresenceRecord } from "./presence";
import { LiveDot } from "./LiveDot";

/** Query param that makes the application page ask this applicant to share. */
export const VIEW_LIVE_PARAM = "view_live";
/** Marks the row action, so the table's own row click leaves it alone. */
export const LIVE_VIEW_ROW_ACTION_ATTR = "data-live-view-row-action";

const presenceSummary = (entries: PresenceRecord[]) =>
  entries.length === 1
    ? `In application now · ${entries[0].step}`
    : `In application now · ${entries
        .map((entry) => `${firstNameOf(entry.applicant_name)}: ${entry.step}`)
        .join(", ")}`;

/**
 * A green dot on an application row while one of its applicants is in the
 * applicant app, with "In application now · {step}" on hover.
 */
export const LivePresenceDot = ({
  applicationId
}: {
  applicationId: number;
}) => {
  const entries = useContext(LivePresenceContext).get(applicationId);
  if (!entries?.length) return null;
  const summary = presenceSummary(entries);

  return (
    <Tooltip placement="top-start">
      <TooltipTrigger asChild>
        <span className="flex shrink-0" aria-label={summary} role="img">
          <LiveDot />
        </span>
      </TooltipTrigger>
      <TooltipContent className="z-50 rounded-lg border bg-alpha-800 px-4 py-3 text-sm text-content-1000-inverted">
        {summary}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * "View live" on an application row while an applicant is in the app. It
 * opens the application and asks that applicant to share; with several in the
 * app it opens the application, where each applicant has their own.
 */
export const LiveViewRowAction = ({
  applicationId,
  onViewLive
}: {
  applicationId: number;
  onViewLive: (applicationId: number, applicantId: number | null) => void;
}) => {
  const entries = useContext(LivePresenceContext).get(applicationId);
  if (!entries?.length) return null;
  const applicantId = entries.length === 1 ? entries[0].applicant_id : null;

  return (
    <Button
      {...{ [LIVE_VIEW_ROW_ACTION_ATTR]: "" }}
      className="shrink-0"
      size={ButtonSize.XS}
      variant={ButtonVariant.BASE}
      onClick={(event) => {
        event.stopPropagation();
        onViewLive(applicationId, applicantId);
      }}
    >
      View live
    </Button>
  );
};
